import { formatInTimeZone } from 'date-fns-tz';
import { UploadData } from '../../../store/uploadStore';

/**
 * The date `mark-loaded` files a scheduled upload under.
 *
 *   exifDate = the date the pilot confirmed in the modal, when it produced one
 *              otherwise the max site-local date over the upload's *precise* photos
 *              otherwise omitted
 *
 * The pilot's answer takes precedence over the photos, including any that turn out to carry an absolute
 * timestamp. It is read from the upload record rather than from local state, so a resumed upload keeps
 * it.
 *
 * The max precise date rather than the earliest, so a multi-day flight lands on the day it finished.
 * Imprecise photos are excluded rather than used as a fallback, for two reasons: a single offsetless
 * file would otherwise outrank a set of GPS-timestamped ones, which is the shape of a scan upload
 * carrying an embedded stitched panorama; and `markImageValidationFailed` puts the file's mtime in
 * `takenAt`, which is not a photo timestamp.
 *
 * When exifDate is omitted, `pilot-api` skips both the move and the schedule update.
 */
export const resolveExifDate = (upload: UploadData): string | undefined => {
  const { timezone } = upload.meta;

  if (!upload.upload) {
    return undefined;
  }

  if (upload.upload.pilotConfirmedDate) {
    return upload.upload.pilotConfirmedDate;
  }

  if (!timezone) {
    return undefined;
  }

  const localDates = upload.upload.photos
    .filter((p) => p.fileCategory !== 'non-image' && p.takenAt && p.datePrecise)
    .map((p) => formatInTimeZone(p.takenAt!, timezone, 'yyyy-MM-dd'));

  return localDates.length ? localDates.reduce((max, d) => (d > max ? d : max)) : undefined;
};
